import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Grid,
  Skeleton,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { RateAnalysisCard } from 'src/App/HOC/Cards';
import {
  COLOR_PRIMARY_1,
  COLOR_TEXT_2,
  COLOR_WARNING,
  HISTORICAL_COLORS_GRADIENT,
  HISTORICAL_COLORS_GRADIENT_REVENUE,
} from 'src/colors';
import { COLOR_PRIMARY_DARK, PAYMENT_MODAL_GREEN, PAYMENT_MODAL_GREEN_HOVER } from 'src/const';
import { RechartsDataFormat } from 'src/interfaces/idashboard';

const GranularDataCard = ({
  metric,
  name,
  dailyData,
  weeklyData,
  monthlyData,
  unit,
  isMarketLocked,
  showNoFilter,
  currency,
  isApiFetching,
  isMobileScreen,
  isDisabled,
  handleOnSampleClick,
  barColor,
}: {
  metric: string;
  name: string;
  dailyData: { [index: string]: number | undefined | null }[] | null;
  weeklyData: { [index: string]: number | undefined | null }[] | null;
  monthlyData: { [index: string]: number | undefined | null }[] | null;
  unit: { symbol: string; isCurrency: boolean };
  isMarketLocked: boolean;
  showNoFilter?: boolean;
  currency: string;
  isApiFetching: boolean;
  isMobileScreen?: boolean;
  isDisabled: boolean;
  handleOnSampleClick?: () => void;
  barColor?: string;
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [dateList, setDateList] = useState<string[]>([]);
  const [metricData, setMetricData] = useState<RechartsDataFormat[]>([]);
  const [monthlyMetricData, setMonthlyMetricData] = useState<RechartsDataFormat[]>([]);
  const [granularityType, setGranularityType] = useState<'Daily' | 'Weekly' | 'Monthly'>('Monthly');
  const [activeDaysData, setActiveDaysData] = useState<RechartsDataFormat[]>([]);
  const getCurrentGranularityObject = (granularityType: string | null) => {
    if (granularityType === 'Daily') {
      return dailyData;
    } else if (granularityType === 'Weekly') {
      return weeklyData;
    } else if (granularityType === 'Monthly') {
      return monthlyData;
    } else {
      return {};
    }
  };

  const getDateArray = currentGranularityObject => {
    return Object.keys(currentGranularityObject);
  };

  const processData = (
    key: string,
    // setValue: React.Dispatch<React.SetStateAction<RechartsDataFormat[]>>,
    granularityType: string
  ) => {
    const tmpGranularityMetricData: RechartsDataFormat[] = [];
    const currentGranularityObject = getCurrentGranularityObject(granularityType);

    if (!currentGranularityObject) {
      return tmpGranularityMetricData;
    }

    const tmpDateList = getDateArray(currentGranularityObject);
    setDateList(tmpDateList);

    // console.log("Debug: ", tmpDateList, tmpGranularityMetricData, currentGranularityObject);
    if (key === 'occupancy_rate' && granularityType === 'Daily') {
      tmpDateList.forEach(date =>
        tmpGranularityMetricData.push({
          date: date,
          [key]: currentGranularityObject[date][key],
        })
      );
    } else if (key === 'active_listings_count') {
      tmpDateList.forEach(date =>
        tmpGranularityMetricData.push({
          date: date,
          [key]: currentGranularityObject[date][key],
        })
      );
    } else {
      tmpDateList.forEach(date => {
        const granularityObjectToPush: any = {
          date: date,
          [`${key}25`]: currentGranularityObject[date][`${key}25`],
          [`${key}50`]: currentGranularityObject[date][`${key}50`],
          [`${key}75`]: currentGranularityObject[date][`${key}75`],
          [`${key}90`]: currentGranularityObject[date][`${key}90`],
        };
        if (currentGranularityObject[date].endDate) {
          granularityObjectToPush.endDate = currentGranularityObject[date].endDate;
        }
        tmpGranularityMetricData.push(granularityObjectToPush);
      });
    }
    // setValue(tmpGranularityMetricData);
    return tmpGranularityMetricData;
    // console.log('Daily Data: ', currentGranularityObject, tmpGranularityMetricData);
    // console.log("Debug: ", tmpDateList, tmpGranularityMetricData, currentGranularityObject);
  };

  const getMetaData = (prefix: string, name: string, noPercentile: boolean = false): any => {
    if (noPercentile) {
      return {
        [prefix]: {
          name: name,
          color: COLOR_PRIMARY_1,
        },
      };
    }

    const tempMetaDataObj = {
      [prefix]: {
        name: 'avg',
        color: COLOR_PRIMARY_1,
      },
      [`${prefix}25`]: {
        name: `25th percentile`,
        // color: HISTORICAL_COLORS_GRADIENT[0],
        color: HISTORICAL_COLORS_GRADIENT_REVENUE[0],
      },
      [`${prefix}50`]: {
        name: `50th percentile`,
        color: HISTORICAL_COLORS_GRADIENT_REVENUE[1],
      },
      [`${prefix}75`]: {
        name: `75th percentile`,
        color: HISTORICAL_COLORS_GRADIENT_REVENUE[2],
      },
      [`${prefix}90`]: {
        name: `90th percentile`,
        color: HISTORICAL_COLORS_GRADIENT_REVENUE[3],
      },
    };

    // if(metric === 'occupancy_rate' && granularityType === 'Daily'){
    //   tempMetaDataObj[prefix]= {
    //     name: 'avg',
    //     color: COLOR_PRIMARY_1,
    //   }
    // }

    return tempMetaDataObj;
  };

  useEffect(() => {
    const tmpGranularityMetricData = processData(metric, granularityType);
    setMetricData(tmpGranularityMetricData);
    setMonthlyMetricData(processData(metric, 'Monthly'));
    const tempActiveDaysData = processData('active_listings_count', granularityType);
    setActiveDaysData(tempActiveDaysData);

    setIsLoading(false);
  }, [isLoading, granularityType, isApiFetching, monthlyData, dailyData, weeklyData, currency]);

  const GranularityTypeSwitch = ({
    values,
    setGranularityType,
    float,
  }: {
    values: ('Daily' | 'Weekly' | 'Monthly')[];
    setGranularityType: React.Dispatch<React.SetStateAction<'Daily' | 'Weekly' | 'Monthly'>>;
    float: string;
  }) => {
    const buttonStyle: any = {
      background: PAYMENT_MODAL_GREEN,
      borderRadius: 200,
      color: '#000',
      border: 'none !important',
      textTransformation: 'capitalize',
      transition: 'background 0.3s ease',
      '&:hover': {
        background: PAYMENT_MODAL_GREEN_HOVER,
      },
    };
    return (
      <ButtonGroup
        variant="contained"
        sx={{
          borderRadius: 200,
          width: '256px',
          background: '#fff',
          float,
          padding: '2px',
          position: 'relative',
          top: '17px',
          right: 'calc((100% - 256px) / 2)',
        }}
      >
        {values.map((data, i) => (
          <Button
            key={i}
            onClick={() => {
              // setGranularityType(data);
            }}
            sx={{
              ...buttonStyle,
              background: granularityType === data ? PAYMENT_MODAL_GREEN : '#fff',
              color: granularityType === data ? '#fff' : '#000',
            }}
          >
            {data}
          </Button>
        ))}
      </ButtonGroup>
    );
  };

  const CustomTooltip = ({
    payload,
    date,
    formatDate,
    showMedian,
    selectedFilters,
    granularityType,
  }: {
    payload: any;
    date: Date;
    formatDate: (dateString: Date, showDay?: boolean) => string;
    showMedian: boolean;
    selectedFilters: string[];
    granularityType?: 'Daily' | 'Weekly' | 'Monthly';
  }) => {
    return (
      <Card sx={{ borderRadius: 3, boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.25)' }}>
        <CardContent
          sx={{
            background: 'white',
            width: '300px',
            // height: '100px',
            overflowX: 'hidden',
          }}
        >
          <Typography
            variant="h6"
            sx={{ marginBottom: 2, fontWeight: 'bold', color: COLOR_PRIMARY_DARK }}
          >
            {formatDate(date, true)}
            {payload[0]?.payload?.endDate && ` - ${formatDate(payload[0].payload.endDate, true)}`}
          </Typography>
          {payload &&
            payload.map(currentPayload => {
              return (
                <Typography
                  variant="body1"
                  sx={{ marginBottom: 2, fontWeight: 'bold', color: COLOR_TEXT_2 }}
                >
                  <>
                    {getMetaData(metric, name)[currentPayload.dataKey].name}:{' '}
                    {unit.isCurrency && unit.symbol}
                    <span style={{ color: COLOR_PRIMARY_DARK }}>{currentPayload.value}</span>{' '}
                    {!unit.isCurrency && unit.symbol}
                  </>
                </Typography>
              );
            })}
          {Object.keys(payload[0]?.payload || {}).includes('revenue25') &&
            granularityType !== 'Monthly' && (
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                * Cleaning fees not included
              </Typography>
            )}
        </CardContent>
      </Card>
    );
  };

  return (
    <>
      {isLoading || isApiFetching ? (
        <div style={{ width: '100%', height: '100%', background: 'white' }}>
          <Grid
            container
            item
            xs={12}
            direction="column"
            justifyContent="center"
            alignItems="center"
            sx={{ height: '100%', width: '100%' }}
          >
            <Skeleton sx={{ width: '80%', height: '15%' }} animation="wave" />
            <Skeleton sx={{ width: '80%', height: '30%' }} animation="wave" />
            <Skeleton sx={{ width: '80%', height: '10%' }} animation="wave" />
          </Grid>
        </div>
      ) : (
        <>
          {/* {metric !== 'rev_par' && (
            <GranularityTypeSwitch
              values={['Daily', 'Weekly', 'Monthly']}
              float={'right'}
              setGranularityType={setGranularityType}
            />
          )} */}

          <RateAnalysisCard
            title={name}
            titleMedianSwitch=""
            data={metricData}
            monthlyData={monthlyMetricData}
            metaData={getMetaData(metric, name)}
            currency={currency}
            dateList={dateList}
            getMedianData={() => ({
              medianData: [],
              medianDataRange: { minData: 0, maxData: 100 },
            })}
            medianKeys={[]}
            medianColors={{}}
            TooltipBody={CustomTooltip}
            isFilterOpen={isMobileScreen ? false : true}
            hideMedianToggleSwitch={true}
            granularityType={granularityType}
            showNoPercentile={
              (metric === 'occupancy_rate' && granularityType === 'Daily')
              || metric === 'active_listings_count'
            }
            isCurrency={unit.isCurrency}
            activeDaysData={activeDaysData}
            isDisabled={isDisabled}
            isMobileScreen={isMobileScreen}
            handleOnSampleClick={handleOnSampleClick}
            barColor={barColor}
          />
        </>
      )}
    </>
  );
};

export default GranularDataCard;
